import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LocalVars } from '@core/constant/local-storage.constants';
import { RETURN_URL_PARAM, returnUrlExtras } from '@core/helpers/return-url.helper';
import { AuthService } from '@core/services/auth.service';
import { LocalStorageService } from '@core/services/local-storage.service';
import { SessionRedirectService } from '@core/services/session-redirect.service';

export const authGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const ls = inject(LocalStorageService);
  const sessionRedirect = inject(SessionRedirectService);

  // Where they were heading rides along, so logging in puts them there rather than on the
  // dashboard — the difference between an inbound deep link working and being lost. It is also
  // stashed, so the destination survives a login URL that loses its query string.
  if (!authService.isLoggedIn()) {
    sessionRedirect.rememberDestination(state.url);
    return router.createUrlTree(['/login'], returnUrlExtras(state.url));
  }

  const isFirstLogin = ls.getItem(LocalVars.IS_FIRST_LOGIN) === 'true';
  const isForceResetRoute = route.routeConfig?.path === 'force-reset-password';

  if (isFirstLogin && !isForceResetRoute) {
    sessionRedirect.rememberDestination(state.url);
    return router.createUrlTree(['/force-reset-password'], returnUrlExtras(state.url));
  }

  return true;
};

/** Redirects root path to /dashboard if logged in, else to /login */
export const defaultRouteGuard: CanActivateFn = () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const ls = inject(LocalStorageService);
  if (!authService.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }
  const target =
    ls.getItem(LocalVars.IS_FIRST_LOGIN) === 'true' ? '/force-reset-password' : '/dashboard';
  return router.createUrlTree([target]);
};

/** Only allows access when logged in AND is_first_login is true. Otherwise redirects to /login or /dashboard. */
export const firstLoginOnlyGuard: CanActivateFn = () => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const ls = inject(LocalStorageService);
  if (!authService.isLoggedIn()) {
    return router.createUrlTree(['/login']);
  }
  if (ls.getItem(LocalVars.IS_FIRST_LOGIN) !== 'true') {
    return router.createUrlTree(['/login']);
  }
  return true;
};

/** If already logged in, redirect from login/auth pages to dashboard (or change-password if first login) */
export const redirectToDashboardIfLoggedInGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const ls = inject(LocalStorageService);
  if (!authService.isLoggedIn()) {
    return true;
  }
  if (ls.getItem(LocalVars.IS_FIRST_LOGIN) === 'true') {
    return router.createUrlTree(['/force-reset-password'], {
      queryParams: route.queryParams,
    });
  }
  // Someone who signed in elsewhere and then followed a deep link arrives here already logged in;
  // this is their landing, so honour the destination rather than sending them to the dashboard.
  const sessionRedirect = inject(SessionRedirectService);
  return router.parseUrl(
    sessionRedirect.consumeLandingUrl(route.queryParamMap.get(RETURN_URL_PARAM)),
  );
};
